import React, { useReducer, useEffect, useState } from 'react'
import { Auth, API, graphqlOperation } from 'aws-amplify'
import { Link } from "react-router-dom";
import { listOrders } from "../graphql/queries"
import { updateUser } from '../graphql/mutations'
import CustomerOrder from './CustomerOrder'
import OrderSearch, { filterOrders, emptyFilters, hasActiveFilters } from '../components/OrderSearch';
import AdminSettings from '../components/AdminSettings';
import SalesDashboard from '../components/SalesDashboard';
import CanceledOrder from './CanceledOrders';
import DeliveryReadyOrder from './DeliveryReadyOrder'
import DeliveredOrder from './DeliveredOrder'
import AddressBook from '../components/AddressBook'
import CRM from '../components/CRM'
import ProductItem from '../components/ProductItem'
import { WishlistContext } from '../components/contexts/WishlistContext'
import {
  Input,
  Form,
  Dialog,
  Button,
  Loading,
  Notification,
  Message,
  MessageBox
} from 'element-react'
import Error from '../components/Error'
import styled from 'styled-components'
import useForm from '../utils/useForm'
import {
  FETCH_DATA_INIT,
  FETCH_DATA_SUCCESS,
  FETCH_DATA_FAILURE,
  RESET_USER_DATA,
} from '../utils/constants'

const getUser = `query GetUser($id: ID!) {
  getUser(id: $id) {
    id
    username
    email
    registered
    admin
    orders(sortDirection: DESC, limit: 999) {
      items {
        id
        createdAt
        price
        shippingAddress {
          city
          country
          address_state
          address_line1
          address_zip
        }

        products (sortDirection: DESC, limit: 999) {
            items {
              description
              file {
                bucket
                region
                key
              }
              price
              quantity
              shipped
              owner
            }
        }

      }
      nextToken
    }
  }
}
`

const ProfileWrap = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 6px 32px;
  box-sizing: border-box;

  @media screen and (min-width: 480px) {
    padding: 24px 16px 56px;
  }
`;

/* ---------- profile summary ---------- */
const ProfileHero = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  background: linear-gradient(135deg, #112a1d 0%, #153724 50%, #1c4830 100%);
  border: 1px solid rgba(1, 191, 113, 0.15);
  border-radius: 20px;
  padding: 28px;
  color: #fff;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(1, 191, 113, 0.06);
`;

const Avatar = styled.div`
  flex: 0 0 auto;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 1.8rem;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(135deg, #01bf71, #ff9a3c);
  border: 3px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.2);
`;

const HeroInfo = styled.div`
  min-width: 0;
`;

const HeroName = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  letter-spacing: -0.01em;
`;

const HeroEmail = styled.div`
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
  word-break: break-word;
  margin-top: 2px;
`;

const AdminBadge = styled.span`
  margin-left: 0;
  flex: 0 0 auto;
  background: linear-gradient(135deg, #01bf71, #0a8f57);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 800;
  padding: 6px 16px;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(1, 191, 113, 0.25);
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media screen and (min-width: 600px) {
    margin-left: auto;
  }
`;

const DetailsCard = styled.div`
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 20px;
  padding: 8px 24px;
  box-shadow: 0 6px 24px rgba(31, 45, 61, 0.04);
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px 16px;
  padding: 18px 0;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  @media screen and (min-width: 600px) {
    grid-template-columns: 180px minmax(0, 1fr) auto;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8a99a8;
  display: flex;
  align-items: center;
  gap: 8px;
  grid-column: 1 / -1;

  @media screen and (min-width: 600px) {
    grid-column: auto;
  }

  svg {
    color: #01bf71;
    flex-shrink: 0;
  }
`;

const DetailValue = styled.div`
  min-width: 0;
  color: #1f2d3d;
  font-size: 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  word-break: break-word;

  &.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.88rem;
    color: #46586a;
    word-break: break-all;
  }
`;

const Pill = styled.span`
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${props => (props.$ok ? 'rgba(1, 191, 113, 0.12)' : 'rgba(239, 83, 80, 0.12)')};
  color: ${props => (props.$ok ? '#0a8f57' : '#d32f2f')};
`;

const EditBtn = styled.button`
  flex: 0 0 auto;
  background: #01bf71;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 8px 18px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(1, 191, 113, 0.2);
  transition: all 0.15s ease;

  &:hover {
    background: #04a763;
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(1, 191, 113, 0.28);
  }
`;

const DangerCard = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: rgba(239, 83, 80, 0.02);
  border: 1.5px dashed rgba(239, 83, 80, 0.3);
  border-radius: 20px;
  padding: 22px 24px;
  box-shadow: 0 4px 12px rgba(239, 83, 80, 0.01);
`;

const DangerTitle = styled.div`
  font-weight: 800;
  color: #d32f2f;
  margin-bottom: 4px;
  font-size: 1.05rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DangerText = styled.div`
  color: #6a7b8a;
  font-size: 0.9rem;
`;

const DeleteBtn = styled.button`
  flex: 0 0 auto;
  background: #ef5350;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 10px 24px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(239, 83, 80, 0.2);
  transition: all 0.15s ease;

  &:hover {
    background: #d32f2f;
    transform: scale(1.03);
    box-shadow: 0 6px 16px rgba(239, 83, 80, 0.3);
  }
`;

const DashboardLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-height: calc(100vh - 180px);
  margin-top: 10px;

  @media screen and (min-width: 900px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const SidebarOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(17, 28, 36, 0.6);
  backdrop-filter: blur(4px);
  z-index: 999;
  display: ${props => (props.$open ? 'block' : 'none')};

  @media screen and (min-width: 900px) {
    display: none;
  }
`;

const Sidebar = styled.div`
  position: fixed;
  top: 0;
  left: ${props => (props.$open ? '0' : '-280px')};
  width: 280px;
  height: 100vh;
  background: #111c24;
  color: #fff;
  z-index: 1000;
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => (props.$open ? '0 0 24px rgba(0, 0, 0, 0.25)' : 'none')};
  display: flex;
  flex-direction: column;
  padding: 24px;
  box-sizing: border-box;

  @media screen and (min-width: 900px) {
    position: sticky;
    top: 20px;
    left: 0;
    width: 260px;
    height: auto;
    min-height: 600px;
    border-radius: 20px;
    box-shadow: 0 8px 32px rgba(31, 45, 61, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.05);
  }
`;

const SidebarLogo = styled.div`
  font-size: 1.25rem;
  font-weight: 900;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  letter-spacing: -0.01em;

  svg {
    color: #01bf71;
  }
`;

const SidebarClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: #a3b8cc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (min-width: 900px) {
    display: none;
  }
`;

const MenuGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;
`;

const GroupLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 800;
  color: #5a6b7a;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 12px 0 6px 12px;
`;

const SidebarBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  background: ${props => (props.$active ? 'rgba(1, 191, 113, 0.12)' : 'transparent')};
  color: ${props => (props.$active ? '#7ce8bd' : '#a3b8cc')};
  border: none;
  border-radius: 12px;
  font-size: 0.92rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;

  svg {
    color: ${props => (props.$active ? '#01bf71' : '#6a7b8a')};
    transition: color 0.15s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.03);
    color: #fff;

    svg {
      color: #01bf71;
    }
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const HeaderPanel = styled.div`
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 20px;
  padding: 16px 24px;
  box-shadow: 0 4px 18px rgba(31, 45, 61, 0.03);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(31, 45, 61, 0.1);
  background: #fff;
  cursor: pointer;
  color: #6a7b8a;
  transition: all 0.15s ease;

  &:hover {
    color: #01bf71;
    border-color: rgba(1, 191, 113, 0.3);
  }

  @media screen and (min-width: 900px) {
    display: none;
  }
`;

const HeaderTitle = styled.h2`
  margin: 0 0 2px;
  font-size: 1.2rem;
  font-weight: 800;
  color: #1f2d3d;
`;

const HeaderSub = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: #8a99a8;
`;

const SignoutBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(31, 45, 61, 0.15);
  color: #6a7b8a;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 8px 18px;
  border-radius: 999px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.15s ease;

  &:hover {
    color: #d32f2f;
    border-color: rgba(211, 47, 47, 0.3);
    background: rgba(211, 47, 47, 0.03);
  }
`;

const Panel = styled.div`
  animation: fadein 0.2s ease;

  @keyframes fadein {
    from { opacity: 0.4; }
    to { opacity: 1; }
  }
`;

const profilePageReducer = (state, action) => {
  switch (action.type) {
    case FETCH_DATA_INIT:
      return {
        ...state,
        isLoading: true,
        isError: false,
      }
    case FETCH_DATA_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isError: false,
        orders: action.payload.orders,
      }
    case FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, isError: true }
    case RESET_USER_DATA:
      return state
    default:
      throw new Error()
  }
}

const ProfilePage = ({ user, userAttributes }) => {
  const initialState = {
    isLoading: true,
    isError: false,
    orders: [],
  }
  const initialValues = {
    emailDialog: false,
    email: userAttributes ? userAttributes.email : '',
    verificationForm: false,
    verificationCode: '',
    updateProductDialog: false,
  }
  const [state, dispatch] = useReducer(profilePageReducer, initialState)
  const { values, handleChange, handleSubmit } = useForm(initialValues)
  const [isAdmin, setAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!userAttributes) return;
    let isMounted = true
    const order_input = { limit: 1 }
    const getUserOrders = async () => {
      if (isMounted) {
        dispatch({ type: FETCH_DATA_INIT })
      }
      try {
        const input = { id: userAttributes.sub }
        const result = await API.graphql(graphqlOperation(getUser, input))
        
        let orders = []
        if (result.data.getUser) {
          if (result.data.getUser.admin) {
            setAdmin(true);
            const order_result = await API.graphql(graphqlOperation(listOrders, order_input))
            orders = (order_result.data.listOrders && order_result.data.listOrders.items) || []
          } else {
            orders = (result.data.getUser.orders && result.data.getUser.orders.items) || []
          }
        }

        if (isMounted) {
          dispatch({
            type: FETCH_DATA_SUCCESS,
            payload: { orders },
          })
        }
      } catch (error) {
        console.error("Error fetching user profile/orders data", error)
        if (isMounted) {
          dispatch({ type: FETCH_DATA_FAILURE })
        }
      }
    }

    getUserOrders()

    return () => {
      isMounted = false
    }
  }, [userAttributes ? userAttributes.sub : ''])

  const handleDeleteProfile = () => {
    MessageBox.confirm(
      'This will permanently delete your account! Continue?',
      'Attention!',
      {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }
    )
      .then(async () => {
        try {
          await user.deleteUser(() =>
            Notification({
              title: 'Success',
              message: 'Profile successfully deleted',
              type: 'success',
              duration: 1000,
              onClose: () => {
                window.location = '/'
                window.location.reload()
              },
            })
          )
        } catch (error) {
          console.error(error)
        }
      })
      .catch(() => {
        Message({
          type: 'info',
          message: 'Delete Profile cancelled',
        })
      })
  }

  const handleUpdateEmail = async () => {
    try {
      handleChange({ verificationForm: true })
      const updatedAttributes = {
        email: values.email,
      }
      await Auth.updateUserAttributes(user, updatedAttributes)
      Message({
        type: 'info',
        customClass: 'message',
        message: `Verification code sent to ${values.email}`,
      })
    } catch (error) {
      console.error(error)
      Notification.error({
        title: 'Error',
        message: `${error.message} || 'Error updating attribute'}`,
      })
    }
  }

  const userLinks = () => {
    return (
      <div className="card">
        <h4 className="card-header">User Links</h4>
        <ul className="list-group">
          <li className="list-group-item">
            <Link className="nav-link" to="/cart">
              My Cart
            </Link>
          </li>

        </ul>
      </div>
    );
  };



  const handleVerifyEmail = async attribute => {
    // verify code against Amazon Cognito User
    try {
      const result = await Auth.verifyCurrentUserAttributeSubmit(
        attribute,
        values.verificationCode
      )
      // update user email in Amazon Dynamo DB
      if (result === 'SUCCESS') {
        try {
          const input = { id: userAttributes.sub, email: values.email }
          await API.graphql(graphqlOperation(updateUser, { input }))
        } catch (error) {
          console.error(error)
          Notification.error({
            title: 'Error',
            message: `${error.message || 'Error updating email'}`,
          })
        }
      }
      Notification({
        title: 'Success',
        message: 'Email successfully verified',
        type: `${result.toLowerCase()}`,
        duration: 3000,
        onClose: () => {
          window.locaticon.reload()
        },
      })
    } catch (error) {
      console.error(error)
      Notification.error({
        title: 'Error',
        message: `${error.message || 'Error updating email'}`,
      })
    }
  }



  const { isLoading, isError } = state

  if (isLoading) return <Loading fullscreen={true} />
  if (isError) return <Error errors={[{ message: "Failed to load profile orders" }]} />

  const handleSignout = async () => {
    try {
      await Auth.signOut();
      window.location = '/';
    } catch (err) {
      console.error('Error signing out', err);
    }
  }

  const menuGroups = [
    {
      label: 'Personal',
      items: [
        { id: 'summary', label: 'Summary', emoji: '📄', icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        )},
        { id: 'orders', label: 'My Orders', emoji: '🧾', icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="4"/><line x1="8" y1="2" x2="8" y2="4"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        )},
        { id: 'addresses', label: 'Address Book', emoji: '📍', icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        )},
        { id: 'wishlist', label: 'Wishlist & Buy Again', emoji: '❤️', icon: (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
        )},
      ]
    },
    ...(isAdmin
      ? [
          {
            label: 'Store Administration',
            items: [
              { id: 'dashboard', label: 'Sales Dashboard', emoji: '📊', icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
              )},
              { id: 'customers', label: 'Customers (CRM)', emoji: '👥', icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              )},
              { id: 'settings', label: 'Store Settings', emoji: '⚙️', icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              )},
            ]
          },
          {
            label: 'Order Logistics',
            items: [
              { id: 'delivery', label: 'Delivery Queue', emoji: '🚚', icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
              )},
              { id: 'delivered', label: 'Delivered History', emoji: '📦', icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
              )},
              { id: 'canceled', label: 'Canceled Orders', emoji: '✖️', icon: (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              )},
            ]
          }
        ]
      : [])
  ]

  return (
    userAttributes && (
      <ProfileWrap>
        <SidebarOverlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} />
        
        <DashboardLayout>
          {/* Left Sidebar */}
          <Sidebar $open={sidebarOpen}>
            <SidebarClose onClick={() => setSidebarOpen(false)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </SidebarClose>
            
            <SidebarLogo>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
              Bombay Groceries
            </SidebarLogo>

            {menuGroups.map(g => (
              <React.Fragment key={g.label}>
                <GroupLabel>{g.label}</GroupLabel>
                <MenuGroup>
                  {g.items.map(t => (
                    <SidebarBtn
                      key={t.id}
                      type="button"
                      $active={activeTab === t.id}
                      onClick={() => {
                        setActiveTab(t.id)
                        setSidebarOpen(false)
                      }}
                    >
                      {t.icon}
                      {t.label}
                    </SidebarBtn>
                  ))}
                </MenuGroup>
              </React.Fragment>
            ))}
          </Sidebar>

          {/* Right Main Content area */}
          <MainContent>
            {/* Header greeting bar */}
            <HeaderPanel>
              <MenuButton type="button" onClick={() => setSidebarOpen(true)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              </MenuButton>
              
              <div>
                <HeaderTitle>Welcome back, {(user.username || 'Merchant')}</HeaderTitle>
                <HeaderSub>Here is your grocery store dashboard summary.</HeaderSub>
              </div>

              <SignoutBtn type="button" onClick={handleSignout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Sign Out
              </SignoutBtn>
            </HeaderPanel>

            <Panel>
              {activeTab === 'summary' && (
                <>
                  <ProfileHero>
                    <Avatar>{(user.username || 'U').charAt(0).toUpperCase()}</Avatar>
                    <HeroInfo>
                      <HeroName>{user.username}</HeroName>
                      <HeroEmail>{userAttributes.email}</HeroEmail>
                    </HeroInfo>
                    {isAdmin && <AdminBadge>Store admin</AdminBadge>}
                  </ProfileHero>

                  <DetailsCard>
                    <DetailRow>
                      <DetailLabel>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" ry="2"/><line x1="7" y1="8" x2="17" y2="8"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="7" y1="16" x2="13" y2="16"/></svg>
                        Your ID
                      </DetailLabel>
                      <DetailValue className="mono">{userAttributes.sub}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        Username
                      </DetailLabel>
                      <DetailValue>{user.username}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                        Email
                      </DetailLabel>
                      <DetailValue>
                        {userAttributes.email}
                        {userAttributes.email_verified ? (
                          <Pill $ok>Verified</Pill>
                        ) : (
                          <Pill>Unverified</Pill>
                        )}
                      </DetailValue>
                      <EditBtn type="button" onClick={() => handleChange({ emailDialog: true })}>
                        Edit
                      </EditBtn>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                        Phone
                      </DetailLabel>
                      <DetailValue>{userAttributes.phone_number || '—'}</DetailValue>
                    </DetailRow>
                  </DetailsCard>

                  <DangerCard>
                    <div>
                      <DangerTitle>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        Delete Account
                      </DangerTitle>
                      <DangerText>
                        This action will permanently remove your account and order history.
                      </DangerText>
                    </div>
                    <DeleteBtn type="button" onClick={handleDeleteProfile}>
                      Delete
                    </DeleteBtn>
                  </DangerCard>
                </>
              )}

              {activeTab === 'orders' && (
                <>
                  <h2 className="header">Order History</h2>
                  <CustomerOrder user={user} userAttributes={userAttributes} />
                </>
              )}

              {activeTab === 'addresses' && <AddressBook />}

              {activeTab === 'wishlist' && <WishlistTabContent />}

              {isAdmin && activeTab === 'dashboard' && (
                <>
                  <h2 className="header">Sales Dashboard</h2>
                  <SalesDashboard />
                </>
              )}

              {isAdmin && activeTab === 'customers' && (
                <CRM />
              )}

              {isAdmin && activeTab === 'delivery' && (
                <>
                  <h2 className="header">Order History</h2>
                  <DeliveryReadyOrder />
                </>
              )}

              {isAdmin && activeTab === 'delivered' && (
                <>
                  <h2 className="header">Order History</h2>
                  <DeliveredOrder />
                </>
              )}

              {isAdmin && activeTab === 'canceled' && (
                <>
                  <h2 className="header">Order History</h2>
                  <CanceledOrder />
                </>
              )}

              {isAdmin && activeTab === 'settings' && (
                <>
                  <h2 className="header">Store Settings</h2>
                  <AdminSettings />
                </>
              )}
            </Panel>
          </MainContent>
        </DashboardLayout>

        {/* Email Dialog */}
        <Dialog
          size="large"
          customClass="dialog"
          title="Edit Email"
          visible={values.emailDialog}
          onCancel={() => handleChange({ emailDialog: false })}
        >
          <Dialog.Body>
            <Form labelPosition="top">
              <Form.Item label="Email">
                <Input
                  value={values.email}
                  onChange={email => handleChange({ email })}
                />
              </Form.Item>
              {values.verificationForm && (
                <Form.Item label="Enter Verification Code" labelWidth="120">
                  <Input
                    value={values.verificationCode}
                    onChange={verificationCode =>
                      handleChange({ verificationCode })
                    }
                  />
                </Form.Item>
              )}
            </Form>
          </Dialog.Body>
          <Dialog.Footer>
            <Button onClick={() => handleChange({ emailDialog: false })}>
              Cancel
            </Button>
            {!values.verificationForm && (
              <Button type="primary" onClick={() => handleUpdateEmail()}>
                Save
              </Button>
            )}
            {values.verificationForm && (
              <Button
                type="primary"
                onClick={event =>
                  handleSubmit(event, () => handleVerifyEmail('email'))
                }
              >
                Submit
              </Button>
            )}
          </Dialog.Footer>
        </Dialog>
      </ProfileWrap>
    )
  )
}

const WishlistTabContent = () => {
  const { wishlistItems, wishlistCount } = React.useContext(WishlistContext);

  if (wishlistCount === 0) {
    return (
      <div>
        <h2 className="header">Saved Wishlist & Buy Again</h2>
        <EmptyWishlistCard>
          <span role="img" aria-label="heart" style={{ fontSize: '3rem' }}>🤍</span>
          <h3>Your Wishlist is empty</h3>
          <p>Tap the heart icon on any product card or detail page to save items for quick 1-click re-ordering!</p>
          <ExploreLink to="/">Explore Groceries</ExploreLink>
        </EmptyWishlistCard>
      </div>
    );
  }

  return (
    <div>
      <h2 className="header">Saved Wishlist & Buy Again ({wishlistCount})</h2>
      <WishlistGrid>
        {wishlistItems.map(product => (
          <ProductItem key={product.id} product={product} />
        ))}
      </WishlistGrid>
    </div>
  );
};

const EmptyWishlistCard = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(31, 45, 61, 0.08);

  h3 {
    margin: 14px 0 6px;
    color: #1f2d3d;
    font-weight: 800;
  }

  p {
    color: #8a99a8;
    margin-bottom: 24px;
    font-size: 0.95rem;
  }
`;

const ExploreLink = styled(Link)`
  background: #01bf71;
  color: #fff;
  padding: 12px 28px;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 700;
  display: inline-block;
  box-shadow: 0 4px 14px rgba(1, 191, 113, 0.3);
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    color: #fff;
  }
`;

const WishlistGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: 580px) {
    gap: 16px;
  }
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 960px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export default ProfilePage;
